import React, { useEffect, useState, useCallback } from 'react';
import '../admin.css';
import { 
  Typography, Card, Button, message, Row, Col, 
  Table, Tag, Space, Modal, Form, Input, Statistic,
  Progress, Tooltip, Divider, Alert, Result, Select
} from 'antd';
import { 
  KeyOutlined, ApiOutlined, CopyOutlined, DeleteOutlined,
  PlusOutlined, ReloadOutlined, SecurityScanOutlined, MonitorOutlined, CrownOutlined
} from '@ant-design/icons';
import { hasFeatureAccess, getUpgradeMessage } from '../utils/planRestrictions';
import UpgradeModal from '../components/UpgradeModal';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const APIPortal = () => {
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [usage, setUsage] = useState({
    monthly_events: 0,
    monthly_limit: 0,
    api_calls_today: 0,
    rate_limit: 500
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [importForm] = Form.useForm();

  // Load API keys
  const loadApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api-keys`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load API keys');
      const data = await res.json();
      
      setApiKeys(data.keys || []);
    } catch (err) {
      message.error(err.message || 'Error loading API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load usage statistics
  const loadUsage = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/my/api-usage`, {
        headers: { ...authHeaders() }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUsage(data);
      }
    } catch (err) {
      console.error('Error loading usage:', err);
    }
  }, []);

  // Load websites for import
  const loadWebsites = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/websites`, {
        headers: { ...authHeaders() }
      });
      
      if (res.ok) {
        const data = await res.json();
        setWebsites(data.websites || []);
      }
    } catch (err) {
      console.error('Error loading websites:', err);
    }
  }, []);

  // Import third-party API key
  const importThirdPartyKey = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/third-party-keys/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(values)
      });

      if (!res.ok) throw new Error('Failed to import third-party API key');

      await res.json(); // Consume the response
      message.success('Third-party API key imported successfully');
      setImportModalVisible(false);
      importForm.resetFields();
      loadApiKeys();
    } catch (err) {
      message.error(err.message || 'Failed to import third-party API key');
    } finally {
      setLoading(false);
    }
  };

  // Generate new API key
  const generateApiKey = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify(values)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to generate API key');
      }

      const newApiKey = await res.json();
      setNewKey(newApiKey);
      setModalVisible(true);
      form.resetFields();
      loadApiKeys();
      message.success('API key generated successfully');
    } catch (err) {
      message.error(err.message || 'Failed to generate API key');
    } finally {
      setLoading(false);
    }
  };

  // Revoke API key
  const revokeApiKey = async (keyId) => {
    try {
      const res = await fetch(`${apiBase}/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to revoke API key');
      }

      message.success('API key revoked successfully');
      loadApiKeys();
    } catch (err) {
      message.error(err.message || 'Failed to revoke API key');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copied to clipboard`);
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  const fetchCurrentPlan = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/my/current-plan`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentPlan(data);
      } else {
        setCurrentPlan(null);
      }
    } catch (err) {
      console.error('Error fetching plan:', err);
      setCurrentPlan(null);
    }
  }, []);

  useEffect(() => {
    loadApiKeys();
    loadUsage();
    fetchCurrentPlan();
    loadWebsites();
  }, [loadApiKeys, loadUsage, fetchCurrentPlan, loadWebsites]);

  // Check if user has access to API Portal
  const hasAPIAccess = hasFeatureAccess(currentPlan, 'api_access');
  const upgradeMessage = getUpgradeMessage('api_access');

  // If user doesn't have access, show upgrade prompt
  if (!hasAPIAccess && currentPlan) {
    return (
      <div className="abo-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0 }}>API Portal</Title>
        </div>

        <Card>
          <Result
            icon={<CrownOutlined style={{ color: '#722ed1' }} />}
            title={upgradeMessage.title}
            subTitle={upgradeMessage.description}
            extra={[
              <Button
                key="upgrade"
                type="primary"
                size="large"
                onClick={() => setUpgradeModalVisible(true)}
                style={{ background: '#722ed1', borderColor: '#722ed1' }}
              >
                Upgrade to Enterprise
              </Button>
            ]}
          />
        </Card>

        <UpgradeModal
          visible={upgradeModalVisible}
          onClose={() => setUpgradeModalVisible(false)}
          limitType="api_access"
          currentPlan={currentPlan?.plan_name}
        />
      </div>
    );
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'key_name',
      key: 'name',
    },
    {
      title: 'Key',
      dataIndex: 'masked_key',
      key: 'key',
      render: (text) => (
        <Text code style={{ fontSize: 12 }}>
          {text}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'lastUsed',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Revoke Key">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Revoke API Key',
                  content: `Are you sure you want to revoke "${record.key_name}"? This action cannot be undone.`,
                  okText: 'Revoke',
                  okType: 'danger',
                  onOk: () => revokeApiKey(record.id),
                });
              }}
              disabled={record.status !== 'active'}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const usagePercentage = usage.monthly_limit > 0 
    ? Math.round((usage.monthly_events / usage.monthly_limit) * 100) 
    : 0;

  return (
    <div className="abo-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>API Portal</Title>
        <Space>
          <Button 
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setImportModalVisible(true)}
          >
            Import Third-Party Key
          </Button>
          <Button 
            icon={<ReloadOutlined />}
            onClick={() => {
              loadApiKeys();
              loadUsage();
            }}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Usage Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Monthly Events"
              value={usage.monthly_events}
              suffix={`/ ${usage.monthly_limit}`}
              prefix={<ApiOutlined style={{ color: '#3730a3' }} />}
            />
            <Progress 
              percent={usagePercentage} 
              size="small" 
              status={usagePercentage > 90 ? 'exception' : 'normal'}
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="API Calls Today"
              value={usage.api_calls_today}
              prefix={<MonitorOutlined style={{ color: '#059669' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Rate Limit"
              value={usage.rate_limit}
              suffix="/ hour"
              prefix={<SecurityScanOutlined style={{ color: '#d97706' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Active Keys"
              value={apiKeys.filter(k => k.status === 'active').length}
              prefix={<KeyOutlined style={{ color: '#7c3aed' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* API Keys Management */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyOutlined />
            <span>API Keys</span>
          </div>
        }
        extra={
          <Form
            form={form}
            layout="inline"
            onFinish={generateApiKey}
          >
            <Form.Item
              name="name"
              rules={[{ required: true, message: 'Please enter key name' }]}
            >
              <Input placeholder="Key name (e.g., Production)" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={loading}
              >
                Generate Key
              </Button>
            </Form.Item>
          </Form>
        }
        style={{ borderRadius: 14 }}
      >
        <Table
          columns={columns}
          dataSource={apiKeys}
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{
            emptyText: 'No API keys found. Generate your first key to get started.'
          }}
        />
      </Card>

      {/* API Documentation */}
      <Card 
        title="Quick Start"
        style={{ borderRadius: 14, marginTop: 24 }}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Title level={4}>Event Ingestion</Title>
            <Paragraph>
              Send events to our API using your API key:
            </Paragraph>
            <TextArea
              value={`curl -X POST ${apiBase}/api/v1/events \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "event_type": "page_view",
    "website_url": "https://example.com",
    "event_data": {
      "page": "/home",
      "user_agent": "Mozilla/5.0..."
    }
  }'`}
              rows={10}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              readOnly
            />
          </Col>
          <Col xs={24} lg={12}>
            <Title level={4}>Analytics Retrieval</Title>
            <Paragraph>
              Get analytics data from your websites:
            </Paragraph>
            <TextArea
              value={`curl -X GET "${apiBase}/api/v1/analytics/summary" \\
  -H "X-API-Key: YOUR_API_KEY"

# Response:
{
  "summary": {
    "total_events": 1250,
    "unique_sessions": 340,
    "unique_websites": 3
  },
  "categories": [
    {"category": "page_view", "count": 800},
    {"category": "click", "count": 450}
  ],
  "rate_limit": {
    "calls_made": 15,
    "calls_limit": 500,
    "remaining": 485
  }
}`}
              rows={10}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              readOnly
            />
          </Col>
        </Row>
      </Card>

      {/* New Key Modal */}
      <Modal
        title="API Key Generated"
        open={modalVisible && newKey}
        onCancel={() => {
          setModalVisible(false);
          setNewKey(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setModalVisible(false);
            setNewKey(null);
          }}>
            Close
          </Button>
        ]}
        width={700}
      >
        {newKey && (
          <div>
            <Alert
              message="Important: Save these credentials now"
              description="For security reasons, you won't be able to see the full API key or signing secret again."
              type="warning"
              style={{ marginBottom: 16 }}
            />
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>API Key:</Text>
              <div style={{ 
                background: '#f5f5f5', 
                padding: 12, 
                borderRadius: 6, 
                fontFamily: 'monospace',
                fontSize: 12,
                marginTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{newKey.api_key}</span>
                <Button 
                  type="text" 
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(newKey.api_key, 'API Key')}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Signing Secret (for HMAC):</Text>
              <div style={{ 
                background: '#f5f5f5', 
                padding: 12, 
                borderRadius: 6, 
                fontFamily: 'monospace',
                fontSize: 12,
                marginTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{newKey.signing_secret}</span>
                <Button 
                  type="text" 
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(newKey.signing_secret, 'Signing Secret')}
                />
              </div>
            </div>

            <Divider />
            
            <Title level={5}>HMAC Signature Example (Node.js):</Title>
            <TextArea
              value={`const crypto = require('crypto');

const payload = JSON.stringify({
  event_type: "page_view",
  website_url: "https://example.com"
});

const signature = crypto
  .createHmac('sha256', '${newKey.signing_secret}')
  .update(payload)
  .digest('hex');

// Include in X-Signature header`}
              rows={8}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              readOnly
            />
          </div>
        )}
      </Modal>

      <UpgradeModal
        visible={upgradeModalVisible}
        onClose={() => setUpgradeModalVisible(false)}
        limitType="api_access"
        currentPlan={currentPlan?.plan_name}
      />

      {/* Import Third-Party API Key Modal */}
      <Modal
        title="Import Third-Party API Key"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          importForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={importForm}
          layout="vertical"
          onFinish={importThirdPartyKey}
        >
          <Form.Item
            name="website_id"
            label="Select Website"
            rules={[{ required: true, message: 'Please select a website' }]}
          >
            <Select placeholder="Choose a website">
              {websites.map(website => (
                <Option key={website.id} value={website.id}>
                  {website.url}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="provider"
            label="Platform Name"
            rules={[{ required: true, message: 'Please specify the platform name' }]}
          >
            <Select placeholder="Choose platform">
              <Option value="shopify">Shopify</Option>
              <Option value="amazon">Amazon</Option>
              <Option value="google">Google Analytics</Option>
              <Option value="facebook">Facebook</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="key_name"
            label="Key Name"
            rules={[{ required: true, message: 'Please enter a key name' }]}
          >
            <Input placeholder="e.g., Shopify Analytics API" />
          </Form.Item>

          <Form.Item
            name="api_key"
            label="API Key"
            rules={[{ required: true, message: 'Please enter the API key' }]}
          >
            <TextArea 
              rows={3} 
              placeholder="Paste the third-party API key here"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <TextArea 
              rows={2} 
              placeholder="Describe what this API key is used for"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => {
                  setImportModalVisible(false);
                  importForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
              >
                Import Key
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default APIPortal;
